/**
 * The Monk Skin Tone Scale and the optical consequence of it.
 *
 * This module is small and it is the reason the whole system exists. Every
 * other camera-based vitals product treats skin tone as a nuisance variable to
 * be averaged away. Here it is a first-class input, recorded with every
 * reading and reported in every aggregate.
 */

/** The ten published Monk Skin Tone Scale swatches, lightest to darkest. */
export const MONK_SWATCHES = Object.freeze([
  '#f6ede4', '#f3e7db', '#f7ead0', '#eadaba', '#d7bd96',
  '#a07e56', '#825c43', '#604134', '#3a312a', '#292420',
]);

export const MONK_MIN = 1;
export const MONK_MAX = 10;

export function isValidTone(tone) {
  return Number.isInteger(tone) && tone >= MONK_MIN && tone <= MONK_MAX;
}

export function clampTone(tone) {
  const t = Math.round(Number(tone) || MONK_MIN);
  return Math.min(MONK_MAX, Math.max(MONK_MIN, t));
}

/** Normalised melanin proxy in [0, 1] derived from the Monk position. */
export function melanin(tone) {
  return (clampTone(tone) - MONK_MIN) / (MONK_MAX - MONK_MIN);
}

/**
 * Fraction of the pulsatile green-channel modulation that survives absorption
 * and reaches the sensor, relative to the lightest tone.
 *
 * Melanin absorbs strongly at the ~525 nm green wavelength that carries the
 * photoplethysmographic signal, so the AC/DC ratio falls roughly exponentially
 * with melanin content. The decay constant here (3.0) reproduces the SHAPE of
 * the effect reported in the literature. It is a model, not a calibration
 * against any specific published dataset, and it must be replaced with
 * measured coefficients once real skin-stratified validation data exists.
 */
export const ATTENUATION_DECAY = 3.0;

export function signalAttenuation(tone) {
  return Math.exp(-ATTENUATION_DECAY * melanin(tone));
}

/** Grouping used in reporting: Monk 7–10 is the population the field fails. */
export function isUnderservedTone(tone) {
  return clampTone(tone) >= 7;
}

/**
 * Median share of participants by Monk tone across public rPPG datasets.
 *
 * Derived from the 2025 npj Digital Medicine review of 100 peer-reviewed rPPG
 * studies, which found Monk 1-3 at a median ~45% of participants and the
 * Monk 4-10 group below 25%. The per-tone spread within those groups is
 * interpolated — the source reports groups, not individual tones — so treat
 * this as the shape of the imbalance rather than ten precise figures.
 */
export const PUBLISHED_DATASET_SHARE = Object.freeze([17, 15, 13, 10, 9, 8, 7, 8, 7, 6]);

/** Approximate world population share by Monk tone. Illustrative reference line. */
export const WORLD_POPULATION_SHARE = Object.freeze([4, 6, 8, 9, 11, 13, 14, 13, 12, 10]);

/** Share of a count vector attributable to the underserved tones. */
export function underservedShare(countsByTone) {
  const total = countsByTone.reduce((a, b) => a + (b || 0), 0);
  if (!total) return 0;
  const dark = countsByTone.slice(6).reduce((a, b) => a + (b || 0), 0);
  return dark / total;
}
